import { useMutation } from '@tanstack/react-query'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { LuPencil } from 'react-icons/lu'

import FormGroup from '@/components/form/FormGroup'
import TextareaInput from '@/components/form/TextareaInput'
import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import useDialog from '@/hooks/useDialog'
import { useHandleApiError } from '@/hooks/useHandleApiError'
import useNotyf from '@/hooks/useNotyf'
import { LONG_MESSAGE_DURATION } from '@/lib/constants'
import { MUTATION_KEYS } from '@/lib/mutationKeys'
import commercialAgreementTemplateService from '@/lib/services/commercialAgreementTemplateService'

interface Props {
  commercialAgreementTemplateId: number
}

interface FormValues {
  message: string
}

const FORM_FIELDS: Record<keyof FormValues, keyof FormValues> = {
  message: 'message',
}

const AmendTemplatedAgreementButton: React.FC<Props> = ({
  commercialAgreementTemplateId,
}) => {
  const notyf = useNotyf()
  const handleApiError = useHandleApiError()
  const amendDialog = useDialog()

  const requestAmendmentMutation = useMutation({
    mutationKey: MUTATION_KEYS.requestCommercialAgreementTemplateAmendment(),
    mutationFn: ({
      values,
    }: {
      values: FormValues
      formikHelpers: FormikHelpers<FormValues>
    }) =>
      commercialAgreementTemplateService.requestAmendment(
        commercialAgreementTemplateId,
        {
          message: values.message,
        },
      ),
    onSuccess: () => {
      notyf.open({
        type: 'success',
        message: 'Amendment request successfully submitted',
        duration: LONG_MESSAGE_DURATION,
      })
      amendDialog.hideDialog()
    },
    onError: (error, variables) =>
      handleApiError({ error, formikHelpers: variables.formikHelpers }),
  })

  const initialValues: FormValues = {
    message: '',
  }

  const isProcessing = requestAmendmentMutation.isPending

  return (
    <>
      <Dialog
        open={amendDialog.isShowingDialog}
        onHide={amendDialog.hideDialog}
      >
        <Button
          variant="outline-primary"
          className="w-full sm:w-auto"
          size="lg"
          onClick={amendDialog.showDialog}
        >
          <LuPencil />
          Request changes
        </Button>

        <DialogContent>
          <Formik
            initialValues={initialValues}
            onSubmit={(values, formikHelpers) =>
              requestAmendmentMutation.mutate({ values, formikHelpers })
            }
          >
            <Form>
              <DialogHeader>
                <DialogTitle>Request changes</DialogTitle>
              </DialogHeader>

              <DialogBody className="pt-4">
                <FormGroup>
                  <TextareaInput
                    autoFocus
                    label="What changes would you like to be made to this agreement?"
                    className="h-25!"
                    name={FORM_FIELDS.message}
                    isDisabled={isProcessing}
                  />
                </FormGroup>
              </DialogBody>

              <DialogFooter>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={amendDialog.hideDialog}
                >
                  Close
                </Button>

                <ActionButton
                  variant="primary"
                  isProcessing={isProcessing}
                  type="submit"
                >
                  Submit
                </ActionButton>
              </DialogFooter>
            </Form>
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default AmendTemplatedAgreementButton
